import { ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@predoc/cow';
import { ExternalLink, Info } from 'lucide-react';
import { ProfileBadge } from '@/components/ProfileBadge';
import { SortableHeader } from '@/components/SortableHeader';
import type { EfaxQueueItem } from '../types';
import { formatReceivedAt, getReceivedAtTooltipCopy } from '../utils/efax-date';
import {
  getProviderNames,
  getProviderFacility,
  getRecipientFacility,
  getRecipientProviders,
} from '../utils/efax-item-helpers';
import { DocumentPickerCell } from '../components/cells/DocumentPickerCell';
import { EfaxActionDropdown } from '../components/cells/EfaxActionDropdown';
import { FacilityCell } from '../components/cells/FacilityCell';
import { IdentifyingCell } from '../components/cells/IdentifyingCell';
import { PatientCell } from '../components/cells/PatientCell';
import { ProvidersCell } from '../components/cells/ProvidersCell';
import { EfaxStatusBadge } from '../components/EfaxStatusBadge';

export const createEfaxColumns = (
  onOpenDocuments: (item: EfaxQueueItem, documentId?: string) => void,
  onViewPatient: (item: EfaxQueueItem) => void,
): ColumnDef<EfaxQueueItem>[] => [
  {
    accessorKey: 'receivedAt',
    enableSorting: true,
    sortingFn: 'datetime',
    sortDescFirst: true,
    header: ({ column }) => {
      const tooltip = getReceivedAtTooltipCopy();

      return (
        <SortableHeader column={column}>
          <span className="flex items-center gap-1">
            Received At
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  className="text-muted-foreground size-3.5 shrink-0"
                  aria-label={tooltip}
                />
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          </span>
        </SortableHeader>
      );
    },
    meta: {
      className:
        'sticky left-0 z-20 bg-white transition-colors group-hover:bg-zinc-50',
    },
    cell: ({ row }) => {
      const { date, time } = formatReceivedAt(row.original.receivedAt);

      return (
        <div className="min-w-[120px]">
          <div>{date}</div>
          <div className="text-muted-foreground text-xs">{time}</div>
        </div>
      );
    },
  },
  {
    id: 'document',
    header: 'Document',
    meta: {
      className:
        "sticky left-[150px] z-20 bg-white transition-colors group-hover:bg-zinc-50 after:absolute after:inset-y-0 after:right-0 after:w-px after:content-[''] after:bg-[var(--border)]",
    },
    cell: ({ row }) => {
      const item = row.original;
      return (
        <button
          type="button"
          onClick={() => onOpenDocuments(item, item.recordUploadId)}
          className="min-w-[260px] text-left"
        >
          <span className="text-foreground inline-flex cursor-pointer items-center gap-1 font-medium group-hover:underline">
            {item.document.fileName}
            <ExternalLink className="text-muted-foreground size-3.5 shrink-0" />
          </span>
          <div className="text-muted-foreground text-xs">{item.documentId}</div>
        </button>
      );
    },
  },
  {
    accessorKey: 'status',
    enableSorting: true,
    sortingFn: 'alphanumeric',
    header: ({ column }) => (
      <SortableHeader column={column}>Status</SortableHeader>
    ),
    cell: ({ row }) => (
      <EfaxStatusBadge
        status={row.original.status}
        details={row.original.errorSummary ?? undefined}
        onViewRawDocument={
          row.original.document.sourcePdfAvailable
            ? () => onOpenDocuments(row.original, row.original.recordUploadId)
            : undefined
        }
      />
    ),
  },
  {
    accessorKey: 'priority',
    enableSorting: true,
    sortingFn: (left, right) =>
      Number(left.original.priority !== 'stat') -
      Number(right.original.priority !== 'stat'),
    header: ({ column }) => (
      <SortableHeader column={column}>
        <span className="flex items-center gap-1">
          Priority
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="text-muted-foreground size-3.5 shrink-0" />
            </TooltipTrigger>
            <TooltipContent>
              Priority is submitted by your organization.
            </TooltipContent>
          </Tooltip>
        </span>
      </SortableHeader>
    ),
    cell: ({ row }) =>
      row.original.priority === 'stat' ? (
        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
          STAT
        </span>
      ) : (
        <span className="text-muted-foreground text-sm">Default</span>
      ),
  },
  {
    id: 'output',
    header: 'Output',
    cell: ({ row }) => (
      <DocumentPickerCell
        item={row.original}
        onViewDocument={(documentId) =>
          onOpenDocuments(row.original, documentId)
        }
      />
    ),
  },
  {
    id: 'profile',
    accessorFn: (item) => item.profile?.displayName ?? '',
    enableSorting: true,
    sortingFn: 'alphanumeric',
    header: ({ column }) => (
      <SortableHeader column={column}>Profile</SortableHeader>
    ),
    cell: ({ row }) => (
      <ProfileBadge
        name={row.original.profile?.displayName ?? ''}
        color={row.original.profile?.avatarType}
      />
    ),
  },
  {
    id: 'patient',
    header: 'Patient',
    cell: ({ row }) => (
      <IdentifyingCell status={row.original.status}>
        <PatientCell patient={row.original.patient} />
      </IdentifyingCell>
    ),
  },
  {
    id: 'sendingProviders',
    header: 'Sending Provider(s)',
    cell: ({ row }) => (
      <IdentifyingCell status={row.original.status}>
        <ProvidersCell
          providers={getProviderNames(row.original.sendingProviders)}
        />
      </IdentifyingCell>
    ),
  },
  {
    id: 'sendingFacility',
    header: 'Sending Provider Facility',
    cell: ({ row }) => (
      <IdentifyingCell status={row.original.status}>
        <FacilityCell
          facility={getProviderFacility(row.original.sendingProviders)}
        />
      </IdentifyingCell>
    ),
  },
  {
    id: 'recipientProvider',
    header: 'Recipient Provider(s)',
    cell: ({ row }) => (
      <IdentifyingCell status={row.original.status}>
        <ProvidersCell providers={getRecipientProviders(row.original)} />
      </IdentifyingCell>
    ),
  },
  {
    id: 'recipientFacility',
    header: 'Recipient Provider Facility',
    cell: ({ row }) => (
      <IdentifyingCell status={row.original.status}>
        <FacilityCell facility={getRecipientFacility(row.original)} />
      </IdentifyingCell>
    ),
  },
  {
    id: 'actions',
    header: '',
    meta: {
      className:
        'sticky right-0 z-10 min-w-0 bg-[linear-gradient(90deg,rgba(0,0,0,0)_0%,rgba(255,255,255,1)_42%)]',
    },
    cell: ({ row }) => (
      <EfaxActionDropdown
        item={row.original}
        onOpenDocuments={onOpenDocuments}
        onViewPatient={onViewPatient}
      />
    ),
  },
];
